<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useMapGetter } from 'dashboard/composables/store';
import KaptionContactFirstPalette from 'dashboard/components/kaption/KaptionContactFirst/KaptionContactFirstPalette.vue';
import {
  CONTACT_FIRST_FLAG,
  REPLY_TEMPLATE_FLAG,
} from 'dashboard/components/kaption/KaptionContactFirst/kaptionContactFirstHelpers';
import KaptionNewConversationModal from './KaptionNewConversationModal.vue';

const props = defineProps({
  // Placement-specific styling:
  //  - 'labeled'  : full green pill with label (generic / top bar)
  //  - 'compact'  : round green icon button (compact rail — KaptionSidebarV2)
  //  - 'row'      : green icon button sized to a search-row (KaptionSidebar)
  variant: {
    type: String,
    default: 'labeled',
    validator: value => ['labeled', 'compact', 'row'].includes(value),
  },
});

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const { canUseFeature } = useFeaturePermissions();
const getWhatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const canOpenAdvanced = computed(() =>
  isKaptionFlagEnabled('KAPTION_NEW_CONVERSATION_MODAL')
);
const isContactFirstEnabled = computed(
  () =>
    isKaptionFlagEnabled(CONTACT_FIRST_FLAG) &&
    isKaptionFlagEnabled(REPLY_TEMPLATE_FLAG) &&
    canUseFeature('whatsapp_templates') &&
    (getWhatsAppInboxes.value || []).length > 0
);
// feature-permissions.REPLY_BOX.9 — admins can hide "Nuevo mensaje" per scope.
const isEnabled = computed(
  () =>
    canUseFeature('new_message') &&
    (isContactFirstEnabled.value || canOpenAdvanced.value)
);

const buttonClass = computed(() => {
  const base =
    'flex items-center justify-center bg-n-brand text-white shadow-sm transition-[filter,background-color] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/50';
  switch (props.variant) {
    case 'compact':
      return `${base} size-10 rounded-full`;
    case 'row':
      return `${base} shrink-0 h-7 px-2.5 rounded-2xl`;
    default:
      return `${base} h-8 gap-1.5 px-3 text-sm font-medium rounded-lg`;
  }
});

const iconSize = computed(() =>
  props.variant === 'labeled' ? 'size-4' : 'size-[1.15rem]'
);

const showPalette = ref(false);
const showWizard = ref(false);
// contact-first-conversation.BUTTON.3 — every sidebar entry opens the same
// person-first flow as the chat-list header, with the wizard under Avanzado.
const open = () => {
  if (isContactFirstEnabled.value) showPalette.value = true;
  else showWizard.value = true;
};
const openAdvanced = () => {
  showPalette.value = false;
  showWizard.value = true;
};
</script>

<template>
  <button
    v-if="isEnabled"
    v-tooltip.right="
      variant !== 'labeled'
        ? t('NEW_CONVERSATION_MODAL.NEW_MESSAGE')
        : undefined
    "
    type="button"
    data-testid="new-message-button"
    :aria-label="t('NEW_CONVERSATION_MODAL.NEW_MESSAGE')"
    :class="buttonClass"
    @click="open"
  >
    <span class="i-lucide-message-square-plus" :class="iconSize" />
    <span v-if="variant === 'labeled'">
      {{ t('NEW_CONVERSATION_MODAL.NEW_MESSAGE') }}
    </span>
  </button>
  <KaptionContactFirstPalette
    v-if="isContactFirstEnabled && showPalette"
    :can-open-advanced="canOpenAdvanced"
    @close="showPalette = false"
    @advanced="openAdvanced"
  />
  <Teleport to="body">
    <KaptionNewConversationModal
      v-if="canOpenAdvanced && showWizard"
      @close="showWizard = false"
    />
  </Teleport>
</template>
